import type {
  AgentAPIInput,
  AgentState,
  AgentToolConfigArg,
  AgentTransition,
  HistoryStrategyTransform,
  ProjectSchemaJSON
} from '@takeshape/schema';
import * as agentChatArgs from '@takeshape/schema/resolvers/ai/agent-chat-args.js';
import * as agentGenerateArgs from '@takeshape/schema/resolvers/ai/agent-generate-args.js';
import { getData, getErrors } from 'app/schema-form/selectors';
import type { List } from 'immutable';
import type { SagaIterator } from 'redux-saga';
import { all, call, put, takeLatest } from 'redux-saga/effects';
import { createSelector } from 'reselect';
import { type ActionType, createAction, createReducer } from 'typesafe-actions';
import { setAgentsToolArgDefaults } from '../ai/actions';
import { createToolArgsDefaultsSaga } from '../ai/saga-utils';
import { getActiveSchema } from '../branches/get-active-schema';
import { getDefaultAIService } from '../common/llm';
import type { ImmutableMap } from '../common/utils/immutable-types';
import { CHANGE_DISCRIMINATOR_FIELD, CHANGE_FIELD } from '../schema-form/action-types';
import type { ChangeDiscriminatorFieldAction, ChangeFieldAction } from '../schema-form/actions';
import { setFormData as bindSetFormData } from '../schema-form/actions';
import type { ErrorList, FormState, SchemaFormSlice } from '../schema-form/types';
import { AGENT_EDITOR_FORM_NAME } from './components/agent-editor/constants';
import type {
  AGENT_STATE_EXECUTION_TYPE,
  AgentEditorFormState,
  HISTORY_STRATEGY_TRANSFORM_TYPE
} from './components/agent-editor/types';
import { createAiState } from './util';

const DEFAULT_GENERATE_SUMMARY_SYSTEM_PROMPT =
  `Summarize the following conversation between a user and an assistant. ` +
  `Retain only important information that helps continue the conversation naturally, ` +
  `including user goals, preferences, unresolved questions, and any key facts or ` +
  `instructions already discussed. Omit pleasantries, confirmations, and filler.`;

export const getFormData = (state: SchemaFormSlice) =>
  getData(state, { formName: AGENT_EDITOR_FORM_NAME }) as ImmutableMap<AgentEditorFormState> | undefined;
export const getAgentFormErrors = (state: SchemaFormSlice) =>
  getErrors(state, { formName: AGENT_EDITOR_FORM_NAME }) as ImmutableMap<ErrorList> | undefined;
export const getAgentStateFromForm = createSelector(getFormData, (formData) => formData?.toJS());
export const getAgentHasChatState = createSelector(
  getAgentStateFromForm,
  (agentState) =>
    agentState?.states && Object.values(agentState?.states).some((state) => state.execution.type === 'chat')
);
export const getIsInitialized = createSelector(getFormData, (formData) => Boolean(formData));
export const getStates = createSelector(getFormData, (formData) => formData?.get('states'));
export const getAgentName = createSelector(getFormData, (formData) => formData?.get('name'));

type SetAgentStateExecutionTypePayload = {
  data: unknown;
  projectSchema: ProjectSchemaJSON;
};
type SetAgentStateExecutionTypeMeta = {
  path: string[];
};
export const SET_AGENT_STATE_EXECUTION_TYPE = 'agents/SET_AGENT_STATE_EXECUTION_TYPE';
export const setAgentStateExecutionType = createAction(SET_AGENT_STATE_EXECUTION_TYPE)<
  SetAgentStateExecutionTypePayload,
  SetAgentStateExecutionTypeMeta
>();
export type SetAgentStateExecutionTypeAction = ActionType<typeof setAgentStateExecutionType>;

type SetHistoryStrategyExecutionTypePayload = {
  data: unknown;
  projectSchema: ProjectSchemaJSON;
};
type SetHistoryStrategyExecutionTypeMeta = {
  path: string[];
};
export const SET_HISTORY_STRATEGY_TRANSFORM_TYPE = 'agents/SET_HISTORY_STRATEGY_TRANSFORM_TYPE';
export const setHistoryStrategyTransformType = createAction(SET_HISTORY_STRATEGY_TRANSFORM_TYPE)<
  SetHistoryStrategyExecutionTypePayload,
  SetHistoryStrategyExecutionTypeMeta
>();
export type SetHistoryStrategyExecutionTypeAction = ActionType<typeof setHistoryStrategyTransformType>;

type SetToolArgDefaultsPayload = {
  args: AgentToolConfigArg[];
};
type SetToolArgDefaultsMeta = {
  path: string[];
};
export const SET_TOOL_ARG_DEFAULTS = 'agents/SET_TOOL_ARG_DEFAULTS';
export const setToolArgDefaults = createAction(SET_TOOL_ARG_DEFAULTS)<
  SetToolArgDefaultsPayload,
  SetToolArgDefaultsMeta
>();
export type SetToolArgDefaultsAction = ActionType<typeof setToolArgDefaults>;

export const setFormData = bindSetFormData(AGENT_EDITOR_FORM_NAME, true);

export function* changeFieldDiscriminatorSaga({ payload, meta }: ChangeDiscriminatorFieldAction): SagaIterator {
  const { formName, path } = meta;
  const projectSchema = yield call(getActiveSchema);
  if (projectSchema && formName === AGENT_EDITOR_FORM_NAME) {
    if (path[0] === 'states' && path[2] === 'execution' && path[3] === 'type') {
      yield put(
        setAgentStateExecutionType(
          {
            data: payload,
            projectSchema
          },
          meta
        )
      );
    }
    if (path[0] === 'historyStrategies' && path[2] === 'transform' && path[3] === 'type') {
      yield put(
        setHistoryStrategyTransformType(
          {
            data: payload,
            projectSchema
          },
          meta
        )
      );
    }
  }
}

export const changeFieldSaga = createToolArgsDefaultsSaga(
  AGENT_EDITOR_FORM_NAME,
  setAgentsToolArgDefaults,
  ({ meta: { path } }: ChangeFieldAction) =>
    (path.length === 6 &&
      path[0] === 'states' &&
      path[2] === 'execution' &&
      path[3] === 'tools' &&
      path[5] === 'ref') ||
    (path.length === 6 &&
      path[0] === 'historyStrategies' &&
      path[2] === 'execution' &&
      path[3] === 'tools' &&
      path[5] === 'ref')
);

export const agentEditorSaga = function* () {
  yield all([
    takeLatest(CHANGE_DISCRIMINATOR_FIELD, changeFieldDiscriminatorSaga),
    takeLatest(CHANGE_FIELD, changeFieldSaga)
  ]);
};

/**
 * Apply defaults for state execution
 */
export function handleSetAgentStateExecutionType(
  state: ImmutableMap<FormState<AgentEditorFormState>>,
  action: SetAgentStateExecutionTypeAction
): any {
  const { path } = action.meta;
  const { payload } = action;
  const { data, projectSchema } = payload;
  const aiDefaults = getDefaultAIService(projectSchema);
  const executionType = data as AGENT_STATE_EXECUTION_TYPE;
  const stateId = path[1];
  const agentStatePath = ['data', ...path.slice(0, -2)];
  const transitions = state.getIn([...agentStatePath, 'transitions']) as List<ImmutableMap<AgentTransition>>;

  let defaults: AgentState | undefined;
  if (executionType === 'chat' || executionType === 'generate') {
    const defaultInput = (
      state.getIn(['data', 'api', 'inputs']) as List<ImmutableMap<AgentAPIInput>> | undefined
    )?.find((input) => {
      const args = input?.get('args');
      return args === agentChatArgs.shapeName || args === agentGenerateArgs.shapeName;
    });

    defaults = createAiState({
      id: stateId,
      type: executionType,
      aiDefaults,
      defaultInput: defaultInput?.get('name'),
      includeTransitions: !transitions || transitions.size === 0
    });
  }

  if (defaults) {
    return state.mergeIn(agentStatePath, defaults);
  }
  return state;
}

/**
 * Apply defaults for history strategy transform
 */
export function handleSetHistoryStrategyTransform(
  state: ImmutableMap<FormState<AgentEditorFormState>>,
  action: SetHistoryStrategyExecutionTypeAction
): any {
  const { path } = action.meta;
  const { payload } = action;
  const { data, projectSchema } = payload;
  const aiDefaults = getDefaultAIService(projectSchema);
  const transformType = data as HISTORY_STRATEGY_TRANSFORM_TYPE;

  let defaults: Partial<HistoryStrategyTransform> | undefined;
  if (aiDefaults && transformType === 'generate') {
    defaults = {
      type: 'generate',
      systemPrompt: DEFAULT_GENERATE_SUMMARY_SYSTEM_PROMPT,
      service: aiDefaults.service
    };
    if (aiDefaults.generateModel) {
      defaults.model = aiDefaults.generateModel;
    }
  }

  if (defaults) {
    return state.mergeIn(['data', ...path.slice(0, -1)], defaults);
  }
  return state;
}

export const handleSetToolArgDefaults = (
  state: ImmutableMap<FormState<AgentEditorFormState>>,
  action: SetToolArgDefaultsAction
): ImmutableMap<FormState<AgentEditorFormState>> => {
  return state.setIn(action.meta.path, action.payload.args);
};

export const agentFormReducer = createReducer({} as any, {
  [SET_AGENT_STATE_EXECUTION_TYPE]: handleSetAgentStateExecutionType,
  [SET_HISTORY_STRATEGY_TRANSFORM_TYPE]: handleSetHistoryStrategyTransform,
  [SET_TOOL_ARG_DEFAULTS]: handleSetToolArgDefaults
});
